import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLessonPreview } from '../lib/useLessonPreview.js';
import { practiceReviewPath } from '../lib/workItems.js';

export function LessonFillBanner({ workItemId, onApprovalReady }: { workItemId: string; onApprovalReady?: (ready: boolean) => void }) {
  const [searchParams] = useSearchParams();
  const state = useLessonPreview(workItemId);
  const preview = state.status === 'ready' && state.preview.kind === 'fill' ? state.preview : null;
  const prerequisitesReady = !!preview && preview.prerequisites.length > 0 && preview.prerequisites.every((p) => p.live && p.published && p.served && p.enterable);
  const ready = !!preview && !preview.refusal && preview.stillAPlaceholder && !preview.placeholderChangedSinceImport
    && preview.placement.current && preview.placement.readingReachable && prerequisitesReady;
  useEffect(() => { onApprovalReady?.(ready); }, [ready, onApprovalReady]);

  if (state.status === 'loading') return <p className="mt-2 text-sm text-slate-500">Loading lesson fill details…</p>;
  if (!preview) {
    return <div className="mt-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900" role="alert">
      <p>Could not load lesson fill details. Approval is held until these details can be checked.</p>
      <button type="button" className="mt-2 font-bold underline" onClick={state.retry}>Retry</button>
    </div>;
  }

  const { lessonId, placeholderTitle, newTitle, placement, prerequisites, questions, refusal } = preview;
  const position = placement.previous && placement.next
    ? `, after ${placement.previous.title ?? 'Untitled lesson'}, before ${placement.next.title ?? 'Untitled lesson'}`
    : placement.previous
      ? `, after ${placement.previous.title ?? 'Untitled lesson'}, at the end of the reading`
      : placement.next
        ? `, at the start of the reading, before ${placement.next.title ?? 'Untitled lesson'}`
        : ', at the start and end of the reading';
  const titleChange = newTitle == null
    ? 'Its new title is unavailable.'
    : newTitle === placeholderTitle ? 'It keeps its title.' : `Its title changes to "${newTitle}".`;

  return (
    <section className="mt-2 max-h-60 space-y-2 overflow-auto rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="lesson-fill">
      <p className="font-semibold text-blue-950">
        Fills an empty lesson already in the course, switched off until released — #{lessonId} "{placeholderTitle ?? 'Untitled lesson (title unavailable)'}" in {placement.groupTitle ?? `the reading (title unavailable; reference ${placement.groupId})`}{position}. {titleChange}{' '}
        Shown to learners after: {prerequisites.length ? `${prerequisites.map((p) => p.title ?? 'Untitled lesson').join(', ')}.` : 'No prerequisites.'}
      </p>
      {!ready ? (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-red-900" role="alert">
          <p>Approval is held because this lesson cannot be released as it stands.</p>
          {!preview.stillAPlaceholder ? <p>This lesson is no longer an empty placeholder.</p> : null}
          {preview.placeholderChangedSinceImport ? <p>The empty lesson has changed since it was imported.</p> : null}
          {!placement.current ? <p>The lesson’s place in the course has changed.</p> : null}
          {!placement.readingReachable ? <p>The reading cannot be reached from the course.</p> : null}
          {!prerequisitesReady ? <p>A prerequisite is unavailable to learners or cannot be started. Check the flags below.</p> : null}
          {refusal ? <p>{refusal.message}</p> : null}
          <button type="button" className="mt-2 font-bold underline" onClick={state.retry}>Retry</button>
        </div>
      ) : null}
      <h3 className="font-bold">Prerequisites</h3>
      {prerequisites.length ? <ul>{prerequisites.map((prerequisite) => {
        const flags = [
          !prerequisite.live ? 'Not live' : '',
          !prerequisite.published ? 'Not published' : '',
          !prerequisite.served ? 'Not shown to learners' : '',
          !prerequisite.enterable ? 'Cannot be started' : '',
        ].filter(Boolean);
        return <li key={prerequisite.id}>
          {prerequisite.title ?? 'Untitled lesson'} (#{prerequisite.id})
          {flags.length ? <span className="ml-2 font-semibold text-amber-900">⚠ {flags.join(' · ')}</span> : null}
        </li>;
      })}</ul> : <p>No prerequisites.</p>}
      <h3 className="font-bold">Practice questions</h3>
      {questions.length ? <ul>{questions.map((question) => (
        <li key={question.workItemId}>
          <Link className="text-blue-700 underline" to={practiceReviewPath(question.workItemId, searchParams)}>{question.title || `Practice question (review reference ${question.workItemId})`}</Link>
          <span className="ml-2 text-slate-600">{question.status.replaceAll('_', ' ')}</span>
        </li>
      ))}</ul> : <p>No practice questions.</p>}
    </section>
  );
}
